import type { Command, Due, Item, Label, Note, Project, Section, ViewOption } from "../api/types";
import { type ById, type SyncState, viewOptionKey } from "./state";

/**
 * Optimistic local effects of Sync commands. They only need to look right until the server's
 * answer arrives (the server's rows then replace them), so they are deliberately approximate:
 * e.g. a due `{string}` is shown as typed, ids come from `temp_id`, and unknown commands are no-ops.
 */

type Args = Record<string, unknown>;

const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
const nowIso = () => new Date().toISOString();

function put<T>(map: ById<T>, id: string, row: T): ById<T> {
  return { ...map, [id]: row };
}
function drop<T>(map: ById<T>, ids: Iterable<string>): ById<T> {
  const next = { ...map };
  for (const id of ids) delete next[id];
  return next;
}
function patch<T extends object>(map: ById<T>, id: string, fields: Partial<T>): ById<T> {
  const row = map[id];
  return row ? put(map, id, { ...row, ...fields }) : map;
}

/** Copy only known keys present in args (so `undefined` args don't clobber). */
function pick<T>(args: Args, keys: readonly (keyof T & string)[]): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) if (k in args) (out as Args)[k] = args[k];
  return out;
}

/** Interpret a `due` argument for local display. */
export function localDue(arg: unknown, previous: Due | null): Due | null {
  if (arg === null) return null;
  if (!arg || typeof arg !== "object") return previous;
  const d = arg as Partial<Due> & { string?: string; date?: string };
  if (d.date) {
    return {
      date: d.date,
      timezone: d.timezone ?? null,
      string: d.string ?? d.date,
      lang: d.lang ?? "en",
      is_recurring: d.is_recurring ?? false,
    };
  }
  if (d.string !== undefined) {
    // Unparsed string: keep the previous date until the server answers.
    if (d.string === "") return null;
    return previous ? { ...previous, string: d.string } : null;
  }
  return previous;
}

function descendants(items: ById<Item>, rootId: string): string[] {
  const out: string[] = [];
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop() as string;
    for (const it of Object.values(items)) {
      if (it.parent_id === id) {
        out.push(it.id);
        stack.push(it.id);
      }
    }
  }
  return out;
}

const ITEM_FIELDS = [
  "content",
  "description",
  "priority",
  "labels",
  "is_collapsed",
  "responsible_uid",
  "day_order",
  "duration",
  "order_key",
  "child_order",
  "deadline",
] as const satisfies readonly (keyof Item)[];

const PROJECT_FIELDS = [
  "name",
  "color",
  "is_favorite",
  "is_collapsed",
  "view_style",
  "description",
  "order_key",
  "child_order",
] as const satisfies readonly (keyof Project)[];

export function applyCommand(state: SyncState, cmd: Command): SyncState {
  const a = cmd.preview ? { ...cmd.args, ...cmd.preview } : cmd.args;
  const id = str(a.id) ?? "";
  switch (cmd.type) {
    case "item_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      const parent = str(a.parent_id) ? state.items[a.parent_id as string] : undefined;
      const projectId =
        str(a.project_id) ??
        parent?.project_id ??
        (str(a.section_id) && state.sections[a.section_id as string]?.project_id) ??
        state.user?.inbox_project_id ??
        "";
      const item: Item = {
        id: newId,
        project_id: projectId,
        section_id: str(a.section_id) ?? parent?.section_id ?? null,
        parent_id: str(a.parent_id) ?? null,
        content: str(a.content) ?? "",
        description: str(a.description) ?? "",
        priority: typeof a.priority === "number" ? a.priority : 1,
        due: localDue(a.due, null),
        deadline: (a.deadline as Item["deadline"]) ?? null,
        duration: (a.duration as Item["duration"]) ?? null,
        labels: Array.isArray(a.labels) ? (a.labels as string[]) : [],
        child_order: typeof a.child_order === "number" ? a.child_order : Number.MAX_SAFE_INTEGER,
        order_key: str(a.order_key) ?? "￿",
        day_order: -1,
        is_collapsed: false,
        checked: false,
        is_deleted: false,
        responsible_uid: str(a.responsible_uid) ?? null,
        added_by_uid: state.user?.id ?? null,
        added_at: nowIso(),
        completed_at: null,
        note_count: 0,
      };
      return { ...state, items: put(state.items, newId, item) };
    }
    case "item_update": {
      const prev = state.items[id];
      if (!prev) return state;
      const fields: Partial<Item> = pick<Item>(a, ITEM_FIELDS);
      if ("due" in a) fields.due = localDue(a.due, prev.due);
      return { ...state, items: patch(state.items, id, fields) };
    }
    case "item_move": {
      const prev = state.items[id];
      if (!prev) return state;
      let fields: Partial<Item> = {};
      if ("parent_id" in a && str(a.parent_id)) {
        const parent = state.items[a.parent_id as string];
        fields = { parent_id: a.parent_id as string, section_id: parent?.section_id ?? null };
        if (parent) fields.project_id = parent.project_id;
      } else if ("section_id" in a && str(a.section_id)) {
        const section = state.sections[a.section_id as string];
        fields = { section_id: a.section_id as string, parent_id: null };
        if (section) fields.project_id = section.project_id;
      } else if ("project_id" in a && str(a.project_id)) {
        fields = { project_id: a.project_id as string, section_id: null, parent_id: null };
      }
      let items = patch(state.items, id, fields);
      if (fields.project_id || "section_id" in fields) {
        for (const childId of descendants(items, id)) {
          items = patch(items, childId, {
            project_id: fields.project_id ?? prev.project_id,
            section_id: fields.section_id ?? null,
          });
        }
      }
      return { ...state, items };
    }
    case "item_complete":
    case "item_close": {
      const prev = state.items[id];
      if (!prev) return state;
      // Recurring tasks move to their next date on the server; keep them until it answers.
      if (cmd.type === "item_close" && prev.due?.is_recurring) return state;
      let items = patch(state.items, id, { checked: true, completed_at: nowIso() });
      for (const childId of descendants(items, id)) items = patch(items, childId, { checked: true });
      return { ...state, items };
    }
    case "item_uncomplete":
      return { ...state, items: patch(state.items, id, { checked: false, completed_at: null }) };
    case "item_delete":
      return { ...state, items: drop(state.items, [id, ...descendants(state.items, id)]) };

    case "project_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      const project: Project = {
        id: newId,
        name: str(a.name) ?? "",
        color: str(a.color) ?? "charcoal",
        parent_id: str(a.parent_id) ?? null,
        child_order: typeof a.child_order === "number" ? a.child_order : Number.MAX_SAFE_INTEGER,
        order_key: str(a.order_key) ?? null,
        is_collapsed: false,
        is_archived: false,
        is_deleted: false,
        is_favorite: a.is_favorite === true,
        is_shared: false,
        view_style: str(a.view_style) ?? "list",
        workspace_id: str(a.workspace_id) ?? null,
        role: "CREATOR",
      };
      return { ...state, projects: put(state.projects, newId, project) };
    }
    case "project_update":
      return { ...state, projects: patch(state.projects, id, pick<Project>(a, PROJECT_FIELDS)) };
    case "project_move":
      return { ...state, projects: patch(state.projects, id, { parent_id: str(a.parent_id) ?? null }) };
    case "project_delete":
    case "project_archive": {
      const gone = new Set([id]);
      for (let grew = true; grew; ) {
        grew = false;
        for (const p of Object.values(state.projects)) {
          if (p.parent_id && gone.has(p.parent_id) && !gone.has(p.id)) {
            gone.add(p.id);
            grew = true;
          }
        }
      }
      const items = Object.values(state.items)
        .filter((i) => gone.has(i.project_id))
        .map((i) => i.id);
      const sections = Object.values(state.sections)
        .filter((s) => gone.has(s.project_id))
        .map((s) => s.id);
      return {
        ...state,
        projects: drop(state.projects, gone),
        items: drop(state.items, items),
        sections: drop(state.sections, sections),
      };
    }
    case "project_reorder": {
      let projects = state.projects;
      for (const p of (a.projects as { id: string; child_order: number }[] | undefined) ?? []) {
        projects = patch(projects, p.id, { child_order: p.child_order });
      }
      return { ...state, projects };
    }

    case "section_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      const section: Section = {
        id: newId,
        name: str(a.name) ?? "",
        project_id: str(a.project_id) ?? "",
        section_order: typeof a.section_order === "number" ? a.section_order : Number.MAX_SAFE_INTEGER,
        order_key: str(a.order_key) ?? null,
        is_collapsed: false,
        is_archived: false,
        is_deleted: false,
      };
      return { ...state, sections: put(state.sections, newId, section) };
    }
    case "section_update":
      return {
        ...state,
        sections: patch(state.sections, id, pick<Section>(a, ["name", "is_collapsed", "order_key"])),
      };
    case "section_reorder": {
      let sections = state.sections;
      for (const s of (a.sections as { id: string; section_order: number }[] | undefined) ?? []) {
        sections = patch(sections, s.id, { section_order: s.section_order });
      }
      return { ...state, sections };
    }
    case "section_delete":
    case "section_archive": {
      const items = Object.values(state.items)
        .filter((i) => i.section_id === id)
        .map((i) => i.id);
      return { ...state, sections: drop(state.sections, [id]), items: drop(state.items, items) };
    }

    case "note_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      const note: Note = {
        id: newId,
        item_id: str(a.item_id),
        project_id: str(a.project_id),
        posted_uid: state.user?.id ?? "",
        content: str(a.content) ?? "",
        file_attachment: (a.file_attachment as Note["file_attachment"]) ?? null,
        posted_at: nowIso(),
        is_deleted: false,
      };
      if (note.item_id) {
        const item = state.items[note.item_id];
        return {
          ...state,
          notes: put(state.notes, newId, note),
          items: item ? patch(state.items, item.id, { note_count: (item.note_count ?? 0) + 1 }) : state.items,
        };
      }
      return { ...state, projectNotes: put(state.projectNotes, newId, note) };
    }
    case "note_update":
      return {
        ...state,
        notes: patch(state.notes, id, pick<Note>(a, ["content"])),
        projectNotes: patch(state.projectNotes, id, pick<Note>(a, ["content"])),
      };
    case "note_delete": {
      const note = state.notes[id];
      const item = note?.item_id ? state.items[note.item_id] : undefined;
      return {
        ...state,
        notes: drop(state.notes, [id]),
        projectNotes: drop(state.projectNotes, [id]),
        items: item
          ? patch(state.items, item.id, { note_count: Math.max(0, (item.note_count ?? 1) - 1) })
          : state.items,
      };
    }

    case "label_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      const label: Label = {
        id: newId,
        name: str(a.name) ?? "",
        color: str(a.color) ?? "charcoal",
        item_order: typeof a.item_order === "number" ? a.item_order : Number.MAX_SAFE_INTEGER,
        is_favorite: a.is_favorite === true,
      };
      return { ...state, labels: put(state.labels, newId, label) };
    }
    case "label_update": {
      const prev = state.labels[id];
      let items = state.items;
      const newName = str(a.name);
      if (prev && newName && newName !== prev.name) {
        for (const it of Object.values(items)) {
          if (it.labels.includes(prev.name)) {
            items = patch(items, it.id, { labels: it.labels.map((l) => (l === prev.name ? newName : l)) });
          }
        }
      }
      return {
        ...state,
        items,
        labels: patch(state.labels, id, pick<Label>(a, ["name", "color", "is_favorite", "item_order"])),
      };
    }
    case "label_delete": {
      const prev = state.labels[id];
      let items = state.items;
      if (prev) {
        for (const it of Object.values(items)) {
          if (it.labels.includes(prev.name)) {
            items = patch(items, it.id, { labels: it.labels.filter((l) => l !== prev.name) });
          }
        }
      }
      return { ...state, items, labels: drop(state.labels, [id]) };
    }

    case "filter_add": {
      const newId = cmd.temp_id ?? cmd.uuid;
      return {
        ...state,
        filters: put(state.filters, newId, {
          id: newId,
          name: str(a.name) ?? "",
          query: str(a.query) ?? "",
          color: str(a.color) ?? "charcoal",
          item_order: typeof a.item_order === "number" ? a.item_order : Number.MAX_SAFE_INTEGER,
          is_favorite: a.is_favorite === true,
          is_deleted: false,
        }),
      };
    }
    case "filter_update":
      return {
        ...state,
        filters: patch(state.filters, id, pick(a, ["name", "query", "color", "is_favorite", "item_order"])),
      };
    case "filter_delete":
      return { ...state, filters: drop(state.filters, [id]) };

    case "view_options_set": {
      const key = viewOptionKey(str(a.view_type) ?? "", str(a.object_id));
      const prev = state.viewOptions[key];
      const next = { ...(prev ?? {}), ...a, is_deleted: false } as ViewOption;
      return { ...state, viewOptions: put(state.viewOptions, key, next) };
    }
    case "view_options_delete":
      return {
        ...state,
        viewOptions: drop(state.viewOptions, [viewOptionKey(str(a.view_type) ?? "", str(a.object_id))]),
      };
    case "project_view_options_defaults_set": {
      const pid = str(a.project_id) ?? "";
      const prev = state.pvods[pid];
      return { ...state, pvods: put(state.pvods, pid, { ...(prev ?? { project_id: pid }), ...a, project_id: pid }) };
    }

    case "reminder_delete":
      return { ...state, reminders: drop(state.reminders, [id]) };

    case "user_update": {
      if (!state.user) return state;
      const user = { ...state.user, ...a };
      if (str(a.timezone)) user.tz_info = { ...state.user.tz_info, timezone: a.timezone as string };
      return { ...state, user };
    }
    default:
      return state;
  }
}

/** Ids appearing as `id`, `*_id` or `*_ids` anywhere in args are replaced via `map`. */
export function remapTempIds(args: Args, map: Readonly<Record<string, string>>): Args {
  const walk = (value: unknown, key: string): unknown => {
    if (typeof value === "string") {
      return (key === "id" || key.endsWith("_id")) && map[value] ? map[value] : value;
    }
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === "string" && key.endsWith("_ids") ? (map[v] ?? v) : walk(v, key)));
    }
    if (value && typeof value === "object") {
      const out: Args = {};
      for (const [k, v] of Object.entries(value)) out[k] = walk(v, k);
      return out;
    }
    return value;
  };
  return walk(args, "") as Args;
}
